<template>
  <div class="user-management">
    <el-card shadow="never" class="main-card animate__animated animate__fadeInUp">
      <!-- 搜索栏 + 累计收益（同一行） -->
      <div class="toolbar-row">
        <div class="search-group">
          <el-input
            v-model="searchUserId"
            placeholder="用户ID"
            clearable
            size="small"
            style="width: 140px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-input
            v-model="searchNickname"
            placeholder="搜索昵称"
            prefix-icon="el-icon-search"
            clearable
            size="small"
            style="width: 180px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-input
            v-model="searchInviteCode"
            placeholder="邀请码"
            prefix-icon="el-icon-collection-tag"
            clearable
            size="small"
            style="width: 160px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-select
            v-model="searchStatus"
            placeholder="用户状态"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部" :value="''" />
            <el-option label="未封禁" :value="0" />
            <el-option label="已封禁" :value="1" />
          </el-select>
          <el-select
            v-model="searchRiskLevel"
            placeholder="风险等级"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部风险等级" :value="''" />
            <el-option label="无风险" :value="0" />
            <el-option label="低风险" :value="1" />
            <el-option label="中风险" :value="2" />
            <el-option label="高风险" :value="3" />
          </el-select>
          <el-select
            v-model="searchDeviceRisk"
            placeholder="设备风险"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部设备" :value="''" />
            <el-option label="正常设备" :value="0" />
            <el-option label="风险设备" :value="1" />
          </el-select>
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="refreshList">刷新</el-button>
        </div>
        <div class="earned-summary">
          <i class="el-icon-coin"></i>
          <span class="earned-label">累计总收益</span>
          <span class="earned-value">{{ totalEarned }}</span>
          <span class="earned-unit">金币</span>
        </div>
      </div>

      <div v-if="error" class="error-msg">{{ error }}</div>
      <el-empty
        v-else-if="!loading && users.length === 0"
        :description="searchUserId || searchNickname || searchInviteCode ? '没有符合条件的用户' : '暂无用户数据'"
      />

      <div v-else class="table-wrapper">
        <el-table
          :data="users"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载..."
          class="data-table"
          row-key="id"
          :header-cell-style="{ background: '#fafafa', color: '#303133', fontWeight: 600 }"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="openid" label="OpenID" min-width="160" show-overflow-tooltip />
          <el-table-column prop="nickname" label="昵称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="avatarUrl" label="头像" width="80" align="center">
            <template slot-scope="scope">
              <el-avatar v-if="scope.row.avatarUrl" :key="scope.row.id" :src="scope.row.avatarUrl" :size="32" shape="circle" />
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="inviteCode" label="邀请码" width="100" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" effect="plain">{{ scope.row.inviteCode || '-' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="leaderId" label="代理ID" width="80" align="center" />
          <el-table-column prop="parentId" label="上级ID" width="80" align="center" />
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 0" type="success" size="small" effect="plain">正常</el-tag>
              <el-tag v-else-if="scope.row.status === 1" type="danger" size="small" effect="plain">封禁</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="riskLevel" label="用户风险" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.riskLevel === 1" type="success" size="small">低</el-tag>
              <el-tag v-else-if="scope.row.riskLevel === 2" type="warning" size="small">中</el-tag>
              <el-tag v-else-if="scope.row.riskLevel === 3" type="danger" size="small">高</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="deviceRisk" label="设备风险" width="90" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.deviceRisk === 0" type="success" size="small" effect="plain">正常</el-tag>
              <el-tag v-else-if="scope.row.deviceRisk === 1" type="danger" size="small" effect="plain">风险</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="注册时间" width="160" align="center" />
          <el-table-column label="操作" width="180" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                plain
                icon="el-icon-view"
                @click="showDetail(scope.row)"
              >
                详情
              </el-button>
              <el-button
                v-if="canEdit"
                size="mini"
                :type="scope.row.status === 0 ? 'danger' : 'success'"
                plain
                @click="handleToggleStatus(scope.row)"
              >
                {{ scope.row.status === 0 ? '禁用' : '启用' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrapper">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 用户详情弹窗 -->
    <el-dialog
      title="用户详情"
      :visible.sync="detailDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <div v-loading="detailLoading" element-loading-text="加载中..." class="detail-content">
        <div class="detail-header">
          <el-avatar v-if="detailUser.avatarUrl" :src="detailUser.avatarUrl" :size="56" shape="circle" />
          <div class="detail-name">{{ detailUser.nickname || '-' }}</div>
          <div class="detail-id">ID: {{ detailUser.id }}</div>
        </div>
        <div class="detail-cards">
          <div class="detail-card coin-current">
            <div class="detail-card-label">当前余额</div>
            <div class="detail-card-value">{{ detailUser.coinBalance || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
          <div class="detail-card coin-total">
            <div class="detail-card-label">累计余额</div>
            <div class="detail-card-value">{{ detailUser.totalEarnedCoins || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
        </div>
        <div v-if="detailUser.status === 1 && detailUser.banDes" class="detail-ban-reason">
          <div class="ban-reason-label">封禁原因</div>
          <div class="ban-reason-text">{{ detailUser.banDes }}</div>
        </div>
        <!-- 设备风险详情 -->
        <div v-if="detailUser.deviceRisk !== null && detailUser.deviceRisk !== undefined" class="detail-risk-section">
          <div class="risk-section-title">
            <i class="el-icon-warning-outline"></i>
            设备安全检测
            <el-tag :type="detailUser.deviceRisk === 1 ? 'danger' : 'success'" size="mini" style="margin-left:8px;">
              {{ detailUser.deviceRisk === 1 ? '风险设备' : '正常设备' }}
            </el-tag>
          </div>
          <div v-if="parsedRiskDetail && parsedRiskDetail.length > 0" class="risk-items">
            <div
              v-for="(item, index) in parsedRiskDetail"
              :key="index"
              class="risk-item"
            >
              <div class="risk-item-header">
                <span class="risk-item-label">{{ item.label }}</span>
                <el-tag
                  :type="item.isRisk ? 'danger' : 'success'"
                  size="mini"
                  effect="plain"
                >
                  {{ item.isRisk ? '命中' : '正常' }}
                </el-tag>
              </div>
              <el-tooltip v-if="item.desc" :content="item.desc" placement="top-start" effect="dark">
                <span class="risk-item-desc">{{ item.desc }}</span>
              </el-tooltip>
            </div>
          </div>
          <div v-if="detailUser.deviceRiskDetail" class="risk-raw-json">
            <div class="risk-raw-title" @click="showRawJson = !showRawJson">
              <i :class="showRawJson ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
              原始风险详情 JSON
            </div>
            <pre v-if="showRawJson" class="risk-json-content">{{ formattedRiskJson }}</pre>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 封禁用户弹窗 -->
    <el-dialog
      title="封禁用户"
      :visible.sync="banDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="banFormRef" :model="banForm" :rules="banRules" label-width="100px">
        <el-form-item label="用户">
          <el-input :value="banUser ? banUser.nickname || banUser.id : ''" disabled />
        </el-form-item>
        <el-form-item label="封禁描述" prop="banDes">
          <el-input
            v-model.trim="banForm.banDes"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入封禁原因"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="banDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="handleBanSubmit">确认封禁</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get, put } from '@/utils/http'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      loading: false,
      error: '',
      searchUserId: '',
      searchNickname: '',
      searchInviteCode: '',
      searchStatus: '',
      searchRiskLevel: '',
      searchDeviceRisk: '',
      totalEarned: 0,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      detailLoading: false,
      detailUser: {},
      banDialogVisible: false,
      banUser: null,
      banForm: {
        status: 1,
        banDes: ''
      },
      banRules: {
        banDes: [{ required: true, message: '请输入封禁描述', trigger: 'blur' }]
      },
      showRawJson: false
    }
  },
  computed: {
    // 是否有修改权限（仅超级管理员 role=0）
    canEdit() {
      return parseInt(localStorage.getItem('role')) === 0
    },
    // 解析设备风险详情 JSON，返回可读的风险项列表
    // 风险标签中文释义参照阿里云 SAF 控制台「风险标签释义」共54条
    parsedRiskDetail() {
      if (!this.detailUser.deviceRiskDetail) return []
      let raw = this.detailUser.deviceRiskDetail
      try {
        if (typeof raw === 'string') {
          raw = JSON.parse(raw)
        }
      } catch {
        // 解析失败，当作纯字符串处理
        raw = { tags: raw }
      }

      // 提取标签列表：支持 { tags: 'a,b' }、直接对象、纯字符串
      let tagsArr = []
      if (raw && typeof raw === 'object') {
        if (raw.tags !== undefined && typeof raw.tags === 'string') {
          tagsArr = raw.tags.split(',').filter(Boolean)
        } else if (raw.tags !== undefined && Array.isArray(raw.tags)) {
          tagsArr = raw.tags.filter(Boolean)
        } else {
          // 遍历对象 key，值为 true/1/非空的视为命中
          tagsArr = Object.keys(raw).filter(key => {
            if (key === 'tags' || key === 'deviceRisk' || key === 'deviceRiskDetail') return false
            const val = raw[key]
            return val === true || val === 1 || val === '1' || (typeof val === 'string' && val.trim() !== '')
          })
        }
      }

      // 阿里云 SAF 风险识别 - 全部54个风险标签（来源：SAF控制台「风险标签释义」）
      const riskLabelMap = {
        // ---- Web设备风险类 ----
        rw_0101: { label: '疑似虚拟浏览器', desc: '疑似虚拟浏览器，允许用户在单一设备上模拟出多个独立的浏览器实例。每个实例都有其独立的配置文件、缓存、Cookie、本地存储以及其他浏览器数据，从而实现浏览器指纹的差异化。在社交、电商、网络营销等场景较为常见，黑产借助虚拟浏览器实现多账号操作，以进行诈骗、薅羊毛等恶意行为。' },
        rw_0102: { label: '疑似安装恶意插件', desc: '疑似安装恶意插件，如：一些具有篡改js、篡改浏览器参数、重定向、劫持等功能的插件。' },
        rw_0103: { label: '疑似自动化工具', desc: '疑似自动化工具，检测到设备疑似安装了具有自动化操作、爬虫等功能的工具，如Bot、Node.js等。' },
        rw_0104: { label: '疑似浏览器参数被篡改', desc: '疑似浏览器参数被篡改，通常是为了实现浏览器指纹的篡改。' },
        rw_0105: { label: '疑似js被调试', desc: '疑似js被调试，即使用调试工具来检查、分析和修改JavaScript代码，可实现篡改页面逻辑等目的。' },
        rw_0106: { label: '疑似js代码被篡改', desc: '疑似js代码被篡改，一般指系统核心、自带的JavaScript代码被篡改。' },
        rw_0107: { label: 'Cookies被禁用', desc: 'Cookie被禁用，Cookie是网站为了识别用户、存储用户偏好、追踪用户在网站上的活动等目的而在用户设备上设置的小型文本文件。当Cookie被禁用时，网站将无法向用户的浏览器发送或接收这些数据文件，从而影响到依赖Cookie的功能。' },
        rw_0108: { label: '异常行为轨迹', desc: '异常行为轨迹，基于鼠标运行轨迹识别到的疑似机器操作行为。' },
        rw_0109: { label: '隐身模式', desc: '隐身模式，即"无痕浏览"或"隐私浏览"，是一种特殊的浏览模式，旨在减少用户在本地设备上留下的浏览痕迹。当用户激活这一模式时，浏览器会不保存浏览历史、搜索记录、Cookies等，临时文件也会清除。' },
        rw_0110: { label: '虚拟摄像头', desc: '虚拟摄像头，是指通过软件模拟出来的摄像头设备，非物理摄像头。这种技术允许用户在浏览器环境中（如进行视频通话、直播、在线会议等）提供视频输入，而实际上使用的视频源可以是预先录制的视频文件、屏幕共享内容、动态图像生成，甚至是实时处理过的物理摄像头画面。此类风险常见于人脸识别、诈骗等场景中。' },
        rw_0111: { label: '云手机', desc: '云手机，是用户通过网页浏览器远程访问和控制部署在云端服务器上的虚拟手机。' },
        rw_0112: { label: '疑似爬虫/协议刷', desc: '疑似爬虫/协议刷' },
        rw_0113: { label: '开发者环境', desc: '开发者环境' },
        // ---- 批量行为类 ----
        ra0610: { label: 'IP/设备存在批量刷流量特征', desc: '依赖批量行为检测策略模型识别输出当前请求设备/IP被检测到存在批量行为特征' },
        // ---- 设备风险类 ----
        is_adbEnable: { label: '开启开发者模式', desc: '设备开启开发者模式，在该模式下，用户可以访问和调整很多设备的高级功能和设置，如查看系统日志、模拟地理位置变化、调整动画速度等。' },
        is_autoOperation: { label: '疑似自动化操作', desc: '疑似自动化操作，是指利用特定的工具、软件或脚本，对智能手机进行程序化控制，实现一系列操作的自动执行。如点击屏幕、滑动页面、输入文本、打开应用、执行特定功能、发送消息、抓取数据等。常见工具如：autojs类、自动点击器、按键精灵、触动精灵等。' },
        is_autoOperationProcess: { label: '疑似开启了自动化操作', desc: '疑似自动化操作，是指利用特定的工具、软件或脚本，对智能手机进行程序化控制，实现一系列操作的自动执行。如点击屏幕、滑动页面、输入文本、打开应用、执行特定功能、发送消息、抓取数据等。常见工具如：autojs类、自动点击器、按键精灵、触动精灵等。' },
        is_boardPhone: { label: '安卓板卡', desc: 'Android板卡设备，是黑产常用的虚拟设备。是指搭载Android操作系统的嵌入式硬件平台，通常以电路板（板卡）的形式存在，具备完整的计算、存储、网络通信及输入输出功能。' },
        is_bootUnlocked: { label: 'Bootloader解锁', desc: 'Bootloader为解锁状态，会允许用户绕过制造商的限制，获得对设备底层系统的更深层访问和控制，常见于刷机设备。' },
        is_clickFarmDevice: { label: '疑似设备牧场', desc: '疑似设备牧场' },
        is_cloudIp: { label: '云IP', desc: '云IP（流量推广反作弊服务使用）' },
        is_cloudPhone: { label: '云手机', desc: '云手机设备，是黑产常用的虚拟设备。是一种基于云计算技术的虚拟手机服务。它将传统的手机硬件、操作系统以及相关应用全部迁移至云端服务器上运行，用户通过客户端（如APP或网页）远程连接并操作这个在云端的虚拟手机。云手机的核心特点是将计算和存储资源集中在云端，而用户端仅需提供基本的显示和输入功能，通过网络实时传输指令和画面。' },
        is_debug: { label: '疑似被调试', desc: '疑似被调试' },
        is_deviceCluster_h: { label: '疑似群控设备（高风险）', desc: '群控是通过专门的软、硬件设备，通过网络连接将控制指令下发至多个被控手机，实现对多部智能手机的集中管理和同步操作。' },
        is_deviceCluster_m: { label: '疑似群控设备（中风险）', desc: '群控是通过专门的软、硬件设备，通过网络连接将控制指令下发至多个被控手机，实现对多部智能手机的集中管理和同步操作。' },
        is_emulator: { label: '设备疑似模拟器', desc: '设备模拟器，是黑产常用的虚拟设备。是一种软件程序，它在宿主机（通常是个人电脑）上运行，能够精确地模拟特定类型的真实电子设备（如智能手机、平板电脑、游戏机等）的操作环境、硬件特性以及系统行为。' },
        is_existedHighRiskDevice: { label: '历史存在风险行为的设备', desc: '历史存在风险行为的设备' },
        is_geoLocationTampered: { label: '虚拟定位（安装态）', desc: '虚拟定位' },
        is_geoLocationTamperedProcess: { label: '虚拟定位（运行态）', desc: '设备疑似安装/运行了带有虚拟定位功能的应用' },
        is_hooked: { label: '设备疑似被HOOK', desc: '注入式攻击，是一种恶意利用技术，通过在目标进程中植入恶意代码并操纵其执行流程，旨在实现数据窃取、权限提升、持久化控制、隐蔽通信、系统干扰等恶意目的。' },
        is_idcIp: { label: 'IDC IP', desc: 'IDC/机房IP（流量推广反作弊服务使用）' },
        is_malWare: { label: '安装恶意软件', desc: '安装恶意软件' },
        is_natIp: { label: '出口IP', desc: '出口IP（流量推广反作弊服务使用）' },
        is_newDevice: { label: '疑似新设备', desc: '疑似新设备或恢复出厂设置' },
        is_noSim: { label: '无卡设备', desc: '设备未插SIM卡' },
        is_openVpn: { label: '设备开启VPN', desc: '设备开启VPN，是在互联网上创建一条加密且经过身份伪装的通信通道，旨在加密数据、隐藏真实IP地址、规避地理限制、实现远程访问等。' },
        is_overseasIp: { label: '海外IP', desc: '海外IP（流量推广反作弊服务使用）' },
        is_pluggedInScreenOn: { label: '疑似屏幕常亮', desc: '疑似持续充电且屏幕常亮，用于辅助识别设备牧场行为' },
        is_repack: { label: '重打包（iOS）', desc: '重打包，是对软件安装包进行深度修改并重新封装的过程，目的涵盖合法的功能定制、安全强化、学习研究，也可能被用于恶意目的如植入恶意代码或规避平台规则。' },
        is_rooted: { label: '设备疑似ROOT/越狱', desc: '设备越狱，是指对设备的操作系统进行破解，以获得对系统核心层的完全控制权限（即"root权限"）。这一过程旨在绕过官方对设备预设的安全限制和管理策略，允许用户安装非官方应用或插件（如外挂程序）、修改设备参数（如设备号、IP等）、修改设备文件等。' },
        is_scriptControl: { label: '疑似安装自动化操作工具', desc: '疑似自动化操作，是指利用特定的工具、软件或脚本，对智能手机进行程序化控制，实现一系列操作的自动执行。如点击屏幕、滑动页面、输入文本、打开应用、执行特定功能、发送消息、抓取数据等。常见工具如：autojs类、自动点击器、按键精灵、触动精灵等。' },
        is_systemVirtual: { label: '系统多开环境', desc: '疑似系统自带多开环境，个别智能手机厂商在操作系统层面集成了原生的多开功能，允许用户在同一部手机上创建特定应用的多个独立副本（分身），每个副本可以单独登录并使用不同的账号。区别于第三方多开软件，但也可能被黑产利用。' },
        is_tampered: { label: '疑似篡改设备信息（安卓）', desc: '疑似篡改设备信息/改机，指对手机进行硬、软件层面的非官方、非原厂改动。' },
        is_unusualRom: { label: '非常见ROM', desc: '此设备ROM不常见，通常可能是定制、改机、测试/开发版、小众品牌设备等情况。' },
        is_VCam: { label: '虚拟摄像头（安装态）', desc: '虚拟摄像头' },
        is_VCamProcess: { label: '虚拟摄像头（运行态）', desc: '设备疑似安装/运行了带有虚拟摄像头功能的应用' },
        is_virtual: { label: '疑似多开环境', desc: '疑似多开环境，是指在同一台设备上同时运行同一款应用程序的多个实例，每个实例可以独立登录不同的账号，互不影响地进行操作。这种功能使得用户可以在同一设备上同时使用同一应用的多个账户，而不必频繁切换登录或使用多台设备。' },
        reporting_gap: { label: '上报信息缺失', desc: '核心采集数据未完整上报，检测能力较弱，常见于未按文档规范接入SDK或脱机等风险场景。' },
        time_over: { label: 'Token超时', desc: '风险识别SDK生成的deviceToken生成时间距今超过7天。' },
        token_degraded: { label: '弱网降级Token', desc: '弱网Token，是初始化未完成时生成的降级Token，可维持基本服务，但安全检测能力较弱。常见于未按文档规范接入SDK或遭受特定黑客攻击等场景。' },
        token_invalid: { label: '无效的设备Token', desc: '风险识别SDK生成的deviceToken被截取、篡改或过期。' },
        token_mismatch: { label: 'Token疑似被劫持', desc: 'deviceToken与本次业务查询对应设备不匹配。' },
        token_replay: { label: 'Token重放', desc: '风险识别SDK生成的deviceToken重复向服务端请求，如非自身使用同一Token多次请求，则可能是服务请求被黑产劫持导致。' },
        token_tampered: { label: 'Token被截取/篡改', desc: '风险识别SDK生成的deviceToken被截取或篡改，如非自身请求传输过程中导致的Token被破坏，则可能是服务请求被黑产劫持导致。' }
      }
      const items = []
      tagsArr.forEach(key => {
        const cleanKey = key.trim()
        if (riskLabelMap[cleanKey]) {
          items.push({
            key: cleanKey,
            label: riskLabelMap[cleanKey].label,
            isRisk: true,
            desc: riskLabelMap[cleanKey].desc
          })
        } else {
          items.push({
            key: cleanKey,
            label: cleanKey,
            isRisk: true,
            desc: '未知风险标签'
          })
        }
      })
      return items
    },
    // 格式化后的风险 JSON
    formattedRiskJson() {
      if (!this.detailUser.deviceRiskDetail) return ''
      try {
        const obj = typeof this.detailUser.deviceRiskDetail === 'string'
          ? JSON.parse(this.detailUser.deviceRiskDetail)
          : this.detailUser.deviceRiskDetail
        return JSON.stringify(obj, null, 2)
      } catch {
        return this.detailUser.deviceRiskDetail
      }
    }
  },
  mounted() {
    this.fetchUsers()
    this.fetchTotalEarned()
  },
  methods: {
    async fetchTotalEarned() {
      try {
        const result = await get('/admin/users/total-earned')
        this.totalEarned = (result && result.data != null) ? result.data : 0
      } catch (e) {
        console.error('获取累计总收益失败:', e.message)
      }
    },

    async showDetail(user) {
      this.detailUser = {
        id: user.id,
        nickname: user.nickname,
        avatarUrl: user.avatarUrl
      }
      this.showRawJson = false
      this.detailDialogVisible = true
      this.detailLoading = true
      try {
        const result = await get(`/admin/users/${user.id}/balance`)
        const data = (result && result.data) ? result.data : {}
        this.detailUser = {
          id: data.id || user.id,
          nickname: data.nickname || user.nickname,
          avatarUrl: data.avatarUrl || user.avatarUrl,
          coinBalance: data.coinBalance,
          totalEarnedCoins: data.totalEarned,
          banDes: data.banDes || '',
          status: data.status !== undefined ? data.status : user.status,
          deviceRisk: data.deviceRisk !== undefined ? data.deviceRisk : user.deviceRisk,
          deviceRiskDetail: data.deviceRiskDetail || user.deviceRiskDetail || ''
        }
      } catch (e) {
        this.$message.error(e.message || '获取用户详情失败')
      } finally {
        this.detailLoading = false
      }
    },

    async fetchUsers() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        }
        if (this.searchUserId) {
          params.userId = this.searchUserId
        }
        if (this.searchNickname) {
          params.nickname = this.searchNickname
        }
        if (this.searchInviteCode) {
          params.inviteCode = this.searchInviteCode
        }
        if (this.searchStatus !== '' && this.searchStatus !== null) {
          params.status = this.searchStatus
        }
        if (this.searchRiskLevel !== '' && this.searchRiskLevel !== null) {
          params.riskLevel = this.searchRiskLevel
        }
        if (this.searchDeviceRisk !== '' && this.searchDeviceRisk !== null) {
          params.deviceRisk = this.searchDeviceRisk
        }

        const result = await get('/admin/users', params)
        const data = (result && result.data) ? result.data : {}
        this.users = Array.isArray(data.users) ? data.users : []
        this.pagination.total = data.total || 0
        this.pagination.pageSize = data.pageSize || this.pagination.pageSize
        this.pagination.page = data.page || this.pagination.page
      } catch (e) {
        this.error = e && e.message ? e.message : '获取用户列表失败'
        this.users = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchUsers()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchUsers()
    },

    refreshList() {
      this.searchUserId = ''
      this.searchNickname = ''
      this.searchInviteCode = ''
      this.searchStatus = ''
      this.searchRiskLevel = ''
      this.searchDeviceRisk = ''
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleToggleStatus(user) {
      if (user.status === 0) {
        // 封禁用户：弹出对话框填写封禁描述
        this.banUser = user
        this.banForm.status = 1
        this.banForm.banDes = ''
        this.banDialogVisible = true
        this.$nextTick(() => {
          if (this.$refs.banFormRef) this.$refs.banFormRef.clearValidate()
        })
      } else {
        // 解封用户：直接发送
        this.doUpdateStatus(user, 0)
      }
    },
    async doUpdateStatus(user, status, banDes = null) {
      const actionText = status === 1 ? '封禁' : '解封'
      try {
        const payload = { status }
        if (banDes) payload.banDes = banDes
        const res = await put(`/admin/users/${user.id}`, payload)
        this.$message.success(res.msg || `${actionText}成功`)
        user.status = status
        if (status === 0) user.banDes = null
      } catch (e) {
        this.$message.error(e.message || `${actionText}失败`)
      }
    },
    async handleBanSubmit() {
      try {
        await this.$refs.banFormRef.validate()
      } catch {
        return
      }
      this.banDialogVisible = false
      await this.doUpdateStatus(this.banUser, this.banForm.status, this.banForm.banDes)
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 4px;
}

.main-card {
  border-radius: 8px;
}

/* ===== 工具栏行 ===== */
.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e8ecf1;
}

.search-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
}

/* ===== 累计收益 ===== */
.earned-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f0f9ff 100%);
  border-radius: 8px;
  border: 1px solid #d1fae5;
  white-space: nowrap;
  flex-shrink: 0;
}

.earned-summary .el-icon-coin {
  font-size: 18px;
  color: #e6a23c;
  margin-right: 4px;
}

.earned-label {
  font-size: 13px;
  color: #606266;
  margin-right: 4px;
}

.earned-value {
  font-size: 20px;
  font-weight: 700;
  color: #059669;
  letter-spacing: 0.5px;
}

.earned-unit {
  font-size: 12px;
  color: #909399;
}

/* ===== 错误信息 ===== */
.error-msg {
  padding: 10px 16px;
  margin-bottom: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 13px;
}

/* ===== 表格 ===== */
.table-wrapper {
  margin-top: 4px;
}

.data-table {
  border-radius: 6px;
  overflow: hidden;
}

.data-table::before {
  display: none;
}

.text-muted {
  color: #c0c4cc;
}

/* ===== 分页 ===== */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 8px 0;
}

/* ===== 详情弹窗 ===== */
.detail-content {
  padding: 8px 0;
}

.detail-header {
  text-align: center;
  padding: 12px 0 20px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;
}

.detail-name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-top: 10px;
}

.detail-id {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}

.detail-cards {
  display: flex;
  gap: 16px;
  padding: 0 4px;
}

.detail-card {
  flex: 1;
  text-align: center;
  padding: 20px 12px;
  border-radius: 10px;
  border: 1px solid;
}

.detail-card-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}

.detail-card-value {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.detail-card-unit {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.coin-current {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.coin-current .detail-card-value {
  color: #16a34a;
}

.coin-total {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.coin-total .detail-card-value {
  color: #2563eb;
}

/* ===== 封禁原因 ===== */
.detail-ban-reason {
  margin-top: 16px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.ban-reason-label {
  font-size: 13px;
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 4px;
}

.ban-reason-text {
  font-size: 14px;
  color: #991b1b;
  line-height: 1.5;
}

.dialog-footer {
  text-align: center;
}

/* ===== 设备风险详情 ===== */
.detail-risk-section {
  margin-top: 16px;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
}

.risk-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.risk-section-title .el-icon-warning-outline {
  font-size: 16px;
  color: #e6a23c;
  margin-right: 6px;
}

.risk-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.risk-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  min-width: 180px;
  flex: 1;
}

.risk-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.risk-item-label {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  white-space: nowrap;
}

.risk-item-desc {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.risk-raw-json {
  margin-top: 12px;
  border-top: 1px dashed #dcdfe6;
  padding-top: 10px;
}

.risk-raw-title {
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}

.risk-raw-title:hover {
  color: #409eff;
}

.risk-json-content {
  margin-top: 8px;
  padding: 12px;
  background: #2d2d2d;
  color: #f8f8f2;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

/* ===== 动画 ===== */
.main-card.animate__animated,
.data-table.animate__animated {
  --animate-duration: 0.3s;
}
</style>
